import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Users2, Search, Mail, Briefcase, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function MyTeam() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my-team")
      .then((r) => setData(r.data))
      .catch(() => setData({ direct: [], indirect: [] }))
      .finally(() => setLoading(false));
  }, []);

  const filt = (arr) => {
    if (!query) return arr;
    const q = query.toLowerCase();
    return arr.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.employee_id || "").toLowerCase().includes(q) ||
        (e.designation || "").toLowerCase().includes(q) ||
        (e.department || "").toLowerCase().includes(q)
    );
  };

  if (loading) {
    return <div className="text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  const direct = filt(data?.direct || []);
  const indirect = filt(data?.indirect || []);
  const isManager = ["manager", "super_admin", "hr"].includes(user?.role);

  return (
    <div data-testid="my-team-page">
      <PageHeader
        title="My Team"
        subtitle={
          isManager
            ? "Direct reports plus coders under the Team Leads who report to you."
            : "Coders and analysts mapped under you as Team Lead."
        }
      />

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-3 text-sm">
          <Stat label="Direct" value={data?.direct_count || 0} accent="bg-[#F26B3A]/10 text-[#F26B3A]" />
          {isManager && (
            <Stat label="Through Team Leads" value={data?.indirect_count || 0} accent="bg-[#1E88E5]/10 text-[#1E88E5]" />
          )}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="my-team-search"
            placeholder="Search by name, ID, designation, dept"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-9 w-72"
          />
        </div>
      </div>

      <Section
        title="Direct reports"
        members={direct}
        emptyMessage={
          isManager
            ? "No employees report directly to you yet. Use Team Mapping to assign."
            : "No coders mapped under you yet. Ask your manager to map team members via Team Mapping."
        }
        testIdPrefix="my-direct"
      />

      {isManager && (
        <div className="mt-6">
          <Section
            title="Through your Team Leads"
            members={indirect}
            emptyMessage="No coders mapped under your Team Leads yet."
            testIdPrefix="my-indirect"
          />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`px-2 py-0.5 rounded-full font-mono text-xs ${accent}`}>{value}</span>
      <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
    </div>
  );
}

function Section({ title, members, emptyMessage, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-section`}>
      <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">{title}</div>
      {members.length === 0 ? (
        <Card className="hr-card p-8 text-center text-sm text-gray-500">{emptyMessage}</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {members.map((m) => (
            <Card key={m.id || m.employee_id} className="hr-card p-4 hover:shadow-md transition-shadow" data-testid={`${testIdPrefix}-${m.employee_id}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#F26B3A]/10 text-[#F26B3A] grid place-items-center font-semibold text-sm">
                  {(m.name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0F172A] truncate">{m.name}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{m.employee_id}</div>
                  <div className="text-xs text-gray-600 mt-1 flex items-center gap-1 flex-wrap">
                    <Briefcase className="h-3 w-3 text-gray-400" />
                    <span>{m.designation || "—"}</span>
                    <span className="text-gray-300">·</span>
                    <span>{m.department || "—"}</span>
                  </div>
                  {m.email && (
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-1 truncate">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <span className="truncate">{m.email}</span>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {m.team_lead_name && <Pill label={`TL: ${m.team_lead_name}`} />}
                    {m.reporting_manager_name && <Pill label={`Mgr: ${m.reporting_manager_name}`} />}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({ label }) {
  return <span className="text-[10px] bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">{label}</span>;
}
